import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { investorAccountAPI, investorContactAPI, distributorAPI, teamAPI } from '../services/api';
import { ArrowLeft, Save } from 'lucide-react';

const investorTypes = ['Individual', 'Company', 'LLP', 'Other'];
const yesNo = ['Yes', 'No'];

/**
 * For legacy contacts that only have a single `name` field, split it into
 * first_name / last_name on first load so the new form fields are pre-populated.
 * Keeps the legacy `name` in sync as a derived value on save.
 */
const _backfillContact = (c) => {
  if (!c) return c;
  const first = (c.first_name || '').trim();
  const last = (c.last_name || '').trim();
  if (first || last) return c;
  const legacy = (c.name || '').trim();
  if (!legacy) return c;
  const parts = legacy.split(/\s+/);
  return { ...c, first_name: parts[0] || '', last_name: parts.slice(1).join(' ') };
};

const InvestorAccountForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [contacts, setContacts] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [errors, setErrors] = useState({});
  const [primarySameAsContact, setPrimarySameAsContact] = useState(false);
  const [additionalSameAsContact, setAdditionalSameAsContact] = useState(false);
  const [registeredSameAsCorrespondence, setRegisteredSameAsCorrespondence] = useState(false);
  // Optional reason text captured on edits. Backend stamps this into
  // whichever section-history array (account_info_history /
  // address_history / fatca_history) actually changed on this save, so
  // one input covers all three tabs.
  const [changeReason, setChangeReason] = useState('');
  // Searchable "Link to Investor Contact" picker — replaces the raw native
  // <select> so admins can find a contact by name / email / mobile substring
  // instead of scrolling through the entire AMC's contact list.
  const [contactQuery, setContactQuery] = useState('');
  const [contactPickerOpen, setContactPickerOpen] = useState(false);

  const [form, setForm] = useState({
    account_name: '', date_of_birth_or_incorporation: '', contact_name_id: '',
    type_of_investor: 'Individual', type_of_investor_other: '', mode_of_holding: 'Single', law_under_formed: '',
    pan: '', part_of_first_close: 'No', investor_lead_id: '', distributor_id: '',
    primary_contacts: [], additional_contacts: [],
    correspondence_address: { address: '', city: '', state: '', country: '', zip_code: '', remarks: '' },
    registered_address: { address: '', city: '', state: '', country: '', zip_code: '', remarks: '' },
    fatca_individual: {
      place_of_birth: '', country_of_birth: '', country_of_citizenship: '', us_citizen_or_resident: 'No',
      us_tin: '', country_of_tax_residency: '', tin_type: '', tin: '', remarks_if_tin_unavailable: ''
    },
    fatca_non_individual: {
      is_us_person: 'No', us_tin: '', formed_outside_india: 'No', tax_residency_other_than_india: 'No',
      is_financial_institution: 'No', giin: '', is_publicly_traded: 'No', controlling_persons_not_indian: 'No'
    },
  });

  useEffect(() => {
    loadDropdowns();
    if (isEdit) loadAccount();
  }, [id]);

  // Sync registered address when correspondence address changes and checkbox is active
  useEffect(() => {
    if (registeredSameAsCorrespondence) {
      setForm(prev => ({ ...prev, registered_address: { ...prev.correspondence_address } }));
    }
  }, [form.correspondence_address, registeredSameAsCorrespondence]);

  const loadDropdowns = async () => {
    try {
      const [cRes, dRes, tRes] = await Promise.all([
        investorContactAPI.getAll(), distributorAPI.getAll(), teamAPI.getMembers()
      ]);
      setContacts(cRes.data);
      setDistributors(dRes.data);
      setTeamMembers(tRes.data);
    } catch (err) { console.error(err); }
  };

  const loadAccount = async () => {
    try {
      const res = await investorAccountAPI.getById(id);
      const a = res.data;
      setForm({
        account_name: a.account_name || '',
        date_of_birth_or_incorporation: a.date_of_birth_or_incorporation || '',
        contact_name_id: a.contact_name_id || '',
        type_of_investor: a.type_of_investor || 'Individual',
        type_of_investor_other: a.type_of_investor_other || '',
        mode_of_holding: a.mode_of_holding || 'Single',
        law_under_formed: a.law_under_formed || '',
        pan: a.pan || '',
        part_of_first_close: a.part_of_first_close || 'No',
        investor_lead_id: a.investor_lead_id || '',
        distributor_id: a.distributor_id || '',
        primary_contacts: (a.primary_contacts || []).map(_backfillContact),
        additional_contacts: (a.additional_contacts || []).map(_backfillContact),
        correspondence_address: a.correspondence_address || { address: '', city: '', state: '', country: '', zip_code: '', remarks: '' },
        registered_address: a.registered_address || { address: '', city: '', state: '', country: '', zip_code: '', remarks: '' },
        fatca_individual: a.fatca_individual || { place_of_birth: '', country_of_birth: '', country_of_citizenship: '', us_citizen_or_resident: 'No', us_tin: '', country_of_tax_residency: '', tin_type: '', tin: '', remarks_if_tin_unavailable: '' },
        fatca_non_individual: a.fatca_non_individual || { is_us_person: 'No', us_tin: '', formed_outside_india: 'No', tax_residency_other_than_india: 'No', is_financial_institution: 'No', giin: '', is_publicly_traded: 'No', controlling_persons_not_indian: 'No' },
      });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const getLinkedContact = () => {
    if (!form.contact_name_id) return null;
    return contacts.find(x => x.id === form.contact_name_id) || null;
  };

  // Keep the searchable picker's display in sync with the currently-selected
  // contact — needed after an edit-mode load when contacts + form arrive
  // asynchronously. Only sets the query when it's empty so we don't stomp
  // on the user's active search text.
  useEffect(() => {
    if (!contactQuery && form.contact_name_id && contacts.length > 0) {
      const c = contacts.find((x) => x.id === form.contact_name_id);
      if (c) setContactQuery(`${c.first_name || ''} ${c.last_name || ''}`.trim());
    }
  }, [form.contact_name_id, contacts, contactQuery]);

  const populateContactFromInvestorContact = (type) => {
    const linked = getLinkedContact();
    if (!linked) return;
    const newPerson = {
      id: crypto.randomUUID(),
      salutation: linked.salutation || '',
      first_name: linked.first_name || '',
      last_name: linked.last_name || '',
      name: `${linked.first_name || ''} ${linked.middle_name || ''} ${linked.last_name || ''}`.replace(/\s+/g, ' ').trim(),
      email: linked.email || '',
      mobile_country_code: linked.mobile_country_code || '',
      mobile: linked.mobile || '',
      telephone: linked.telephone || '',
      authorised_signatory: 'No',
      signatory_position: '',
      remarks: ''
    };
    setForm(prev => ({ ...prev, [type]: [newPerson, ...prev[type]] }));
  };

  const handlePrimarySameAsContact = (checked) => {
    setPrimarySameAsContact(checked);
    if (checked) populateContactFromInvestorContact('primary_contacts');
  };

  const handleAdditionalSameAsContact = (checked) => {
    setAdditionalSameAsContact(checked);
    if (checked) populateContactFromInvestorContact('additional_contacts');
  };

  const handleRegisteredSameAsCorrespondence = (checked) => {
    setRegisteredSameAsCorrespondence(checked);
    if (checked) {
      setForm(prev => ({ ...prev, registered_address: { ...prev.correspondence_address } }));
    }
  };

  const handleContactSelect = (contactId) => {
    setForm(prev => ({ ...prev, contact_name_id: contactId }));    if (contactId) {
      const c = contacts.find(x => x.id === contactId);
      if (c) {
        setForm(prev => ({
          ...prev,
          investor_lead_id: prev.investor_lead_id || c.investor_lead_id || '',
          distributor_id: prev.distributor_id || c.distributor_id || '',
        }));
      }
    }
    // Reset auto-populate checkboxes when contact changes
    setPrimarySameAsContact(false);
    setAdditionalSameAsContact(false);
  };

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const setNested = (section, field, value) => {
    setForm(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
  };

  const addContactPerson = (type) => {
    const newPerson = { id: crypto.randomUUID(), name: '', salutation: '', first_name: '', last_name: '', email: '', mobile_country_code: '', mobile: '', telephone: '', whatsapp_number: '', whatsapp_opt_out: false, authorised_signatory: 'No', signatory_position: '', remarks: '' };
    setForm(prev => ({ ...prev, [type]: [...prev[type], newPerson] }));
  };

  const updateContactPerson = (type, idx, field, value) => {
    setForm(prev => {
      const list = [...prev[type]];
      list[idx] = { ...list[idx], [field]: value };
      // Reset signatory_position when authorised_signatory is set to No
      if (field === 'authorised_signatory' && value === 'No') {
        list[idx].signatory_position = '';
      }
      return { ...prev, [type]: list };
    });
  };

  const removeContactPerson = (type, idx) => {
    setForm(prev => ({ ...prev, [type]: prev[type].filter((_, i) => i !== idx) }));
  };

  const validate = () => {
    const e = {};
    if (!form.account_name.trim()) e.account_name = 'Required';
    if (isEdit && !changeReason.trim()) e.change_reason = 'Reason for change is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = { ...form };
      // Keep legacy `name` field in sync with first_name/last_name for backward compatibility
      const _syncName = (c) => {
        const first = (c.first_name || '').trim();
        const last = (c.last_name || '').trim();
        const combined = (first + ' ' + last).trim();
        return combined ? { ...c, name: combined } : c;
      };
      payload.primary_contacts = (payload.primary_contacts || []).map(_syncName);
      payload.additional_contacts = (payload.additional_contacts || []).map(_syncName);
      // Convert empty strings to null for optional UUID/ID fields
      if (!payload.contact_name_id) payload.contact_name_id = null;
      if (!payload.investor_lead_id) payload.investor_lead_id = null;
      if (!payload.distributor_id) payload.distributor_id = null;

      // On edits, the reason is mandatory — forward it to all three
      // section history buckets. The backend only stamps history for
      // sections that actually changed, so passing the same reason to
      // all keys is safe and preserves the "one reason input" UX.
      if (isEdit) {
        payload.change_remarks = {
          account_info: changeReason.trim(),
          contact_address: changeReason.trim(),
          fatca: changeReason.trim(),
        };
      }

      if (isEdit) await investorAccountAPI.update(id, payload);
      else await investorAccountAPI.create(payload);
      navigate('/investors/accounts');
    } catch (err) {
      console.error('Save error:', err);
      alert(err.response?.data?.detail || 'Failed to save');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="text-center py-12">Loading...</div>;

  const isIndividual = form.type_of_investor === 'Individual';
  const hasLinkedContact = Boolean(form.contact_name_id);

  return (
    <div data-testid="investor-account-form-page">
      <button onClick={() => navigate('/investors/accounts')} className="flex items-center text-gray-600 hover:text-gray-900 mb-6" data-testid="back-btn">
        <ArrowLeft size={20} className="mr-2" /> Back to Accounts
      </button>

      <div className="bg-white rounded-xl shadow-md">
        <div className="p-6 border-b">
          <h1 className="text-2xl font-bold text-gray-900" data-testid="form-title">{isEdit ? 'Edit' : 'New'} Investor Account</h1>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-8">

          {/* 1. Account Information */}
          <Section title="Account Information">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Field label="Account Name *" error={errors.account_name}>
                <input type="text" value={form.account_name} onChange={e => set('account_name', e.target.value)} className={inputClass(errors.account_name)} data-testid="account-name-input" />
              </Field>
              <Field label="Date of Birth / Incorporation">
                <input type="date" value={form.date_of_birth_or_incorporation} onChange={e => set('date_of_birth_or_incorporation', e.target.value)} className={inputClass()} data-testid="dob-input" />
              </Field>
              <Field label="Link to Investor Contact">
                {(() => {
                  const q = contactQuery;
                  const setQ = setContactQuery;
                  const filtered = q.trim()
                    ? contacts.filter((c) => {
                        const hay = `${c.first_name || ''} ${c.middle_name || ''} ${c.last_name || ''} ${c.email || ''} ${c.mobile || ''}`.toLowerCase();
                        return hay.includes(q.trim().toLowerCase());
                      })
                    : contacts;
                  const selected = contacts.find((c) => c.id === form.contact_name_id);
                  return (
                    <div className="relative" data-testid="contact-search-wrapper">
                      <input
                        type="text"
                        value={q}
                        onChange={(e) => { setQ(e.target.value); setContactPickerOpen(true); }}
                        onFocus={() => setContactPickerOpen(true)}
                        onBlur={() => setTimeout(() => setContactPickerOpen(false), 150)}
                        placeholder={selected ? `${selected.first_name} ${selected.last_name}` : 'Search contact by name, email or mobile…'}
                        className={inputClass()}
                        data-testid="contact-search-input"
                      />
                      {contactPickerOpen && (
                        <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-gray-300 rounded-lg shadow-lg" data-testid="contact-search-dropdown">
                          {filtered.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-gray-500">No contacts match &quot;{q}&quot;.</div>
                          ) : (
                            <>
                              <button
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => { handleContactSelect(''); setQ(''); setContactPickerOpen(false); }}
                                className="w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-gray-50 border-b"
                                data-testid="contact-clear-selection"
                              >
                                Clear selection
                              </button>
                              {filtered.slice(0, 200).map((c) => (
                                <button
                                  key={c.id}
                                  type="button"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    handleContactSelect(c.id);
                                    setQ(`${c.first_name || ''} ${c.last_name || ''}`.trim());
                                    setContactPickerOpen(false);
                                  }}
                                  className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${form.contact_name_id === c.id ? 'bg-blue-50 font-semibold text-blue-800' : 'text-gray-800'}`}
                                  data-testid={`contact-option-${c.id}`}
                                >
                                  {c.first_name} {c.middle_name ? c.middle_name + ' ' : ''}{c.last_name}
                                  {c.email && <span className="block text-xs text-gray-500 truncate">{c.email}</span>}
                                </button>
                              ))}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </Field>
              <Field label="Type of Investor">
                <select value={form.type_of_investor} onChange={e => set('type_of_investor', e.target.value)} className={inputClass()} data-testid="type-select">
                  {investorTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Mode of Holding">
                <select value={form.mode_of_holding} onChange={e => set('mode_of_holding', e.target.value)} className={inputClass()} data-testid="mode-of-holding-select">
                  <option value="Single">Single</option>
                  <option value="Joint">Joint</option>
                  <option value="Anyone or Survivor">Anyone or Survivor</option>
                </select>
              </Field>
              {form.type_of_investor === 'Other' && (
                <Field label="Type (Other)">
                  <input type="text" value={form.type_of_investor_other} onChange={e => set('type_of_investor_other', e.target.value)} className={inputClass()} />
                </Field>
              )}
              {!isIndividual && (
                <Field label="Law Under Formed">
                  <input type="text" value={form.law_under_formed} onChange={e => set('law_under_formed', e.target.value)} className={inputClass()} data-testid="law-input" />
                </Field>
              )}
              <Field label="PAN">
                <input type="text" value={form.pan} onChange={e => set('pan', e.target.value.toUpperCase())} className={inputClass()} maxLength={10} data-testid="pan-input" />
              </Field>
              <Field label="Part of First Close">
                <select
                  value={form.part_of_first_close || 'No'}
                  onChange={e => set('part_of_first_close', e.target.value)}
                  className={inputClass()}
                  data-testid="first-close-select">
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </Field>
              <Field label="Investor Lead">
                <select value={form.investor_lead_id} onChange={e => set('investor_lead_id', e.target.value)} className={inputClass()} data-testid="lead-select">
                  <option value="">Select</option>
                  {teamMembers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </Field>
              <Field label="Distributor">
                <select value={form.distributor_id} onChange={e => set('distributor_id', e.target.value)} className={inputClass()} data-testid="dist-select">
                  <option value="">Select</option>
                  {distributors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </Field>
            </div>
          </Section>

          {/* 2. Contact & Address */}
          <Section title="Contact & Address">
            <ContactPersonList
              title="Primary Contacts"
              type="primary_contacts"
              list={form.primary_contacts}
              onAdd={addContactPerson}
              onUpdate={updateContactPerson}
              onRemove={removeContactPerson}
              sameAsContact={primarySameAsContact}
              onSameAsContactChange={handlePrimarySameAsContact}
              hasLinkedContact={hasLinkedContact}
            />
            <ContactPersonList
              title="Additional Contacts"
              type="additional_contacts"
              list={form.additional_contacts}
              onAdd={addContactPerson}
              onUpdate={updateContactPerson}
              onRemove={removeContactPerson}
              sameAsContact={additionalSameAsContact}
              onSameAsContactChange={handleAdditionalSameAsContact}
              hasLinkedContact={hasLinkedContact}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <AddressBlock title="Correspondence Address" section="correspondence_address" data={form.correspondence_address} onChange={setNested} />
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-700">Registered / Permanent Address</h3>
                </div>
                <label className="flex items-center gap-2 mb-3 text-sm text-gray-600 cursor-pointer" data-testid="registered-same-checkbox">
                  <input
                    type="checkbox"
                    checked={registeredSameAsCorrespondence}
                    onChange={e => handleRegisteredSameAsCorrespondence(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  Same as Correspondence Address
                </label>
                <div className={registeredSameAsCorrespondence ? 'opacity-60 pointer-events-none' : ''}>
                  <div className="space-y-2">
                    <textarea placeholder="Address" value={form.registered_address.address || ''} onChange={e => setNested('registered_address', 'address', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" rows={2} />
                    <div className="grid grid-cols-2 gap-2">
                      <input type="text" placeholder="City" value={form.registered_address.city || ''} onChange={e => setNested('registered_address', 'city', e.target.value)} className="border rounded px-2 py-1.5 text-sm" />
                      <input type="text" placeholder="State" value={form.registered_address.state || ''} onChange={e => setNested('registered_address', 'state', e.target.value)} className="border rounded px-2 py-1.5 text-sm" />
                      <input type="text" placeholder="Country" value={form.registered_address.country || ''} onChange={e => setNested('registered_address', 'country', e.target.value)} className="border rounded px-2 py-1.5 text-sm" />
                      <input type="text" placeholder="ZIP Code" value={form.registered_address.zip_code || ''} onChange={e => setNested('registered_address', 'zip_code', e.target.value)} className="border rounded px-2 py-1.5 text-sm" />
                    </div>
                    <textarea placeholder="Remarks" value={form.registered_address.remarks || ''} onChange={e => setNested('registered_address', 'remarks', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" rows={2} />
                  </div>
                </div>
              </div>
            </div>
          </Section>

          {/* 3. FATCA / CRS */}
          <Section title="FATCA / CRS Declarations">
            {isIndividual ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Field label="Place of Birth"><input type="text" value={form.fatca_individual.place_of_birth} onChange={e => setNested('fatca_individual', 'place_of_birth', e.target.value)} className={inputClass()} /></Field>
                <Field label="Country of Birth"><input type="text" value={form.fatca_individual.country_of_birth} onChange={e => setNested('fatca_individual', 'country_of_birth', e.target.value)} className={inputClass()} /></Field>
                <Field label="Country of Citizenship"><input type="text" value={form.fatca_individual.country_of_citizenship} onChange={e => setNested('fatca_individual', 'country_of_citizenship', e.target.value)} className={inputClass()} /></Field>
                <Field label="US Citizen / Resident">
                  <select value={form.fatca_individual.us_citizen_or_resident} onChange={e => setNested('fatca_individual', 'us_citizen_or_resident', e.target.value)} className={inputClass()}>{yesNo.map(v => <option key={v}>{v}</option>)}</select>
                </Field>
                {form.fatca_individual.us_citizen_or_resident === 'Yes' && (
                  <Field label="US TIN"><input type="text" value={form.fatca_individual.us_tin} onChange={e => setNested('fatca_individual', 'us_tin', e.target.value)} className={inputClass()} /></Field>
                )}
                <Field label="Country of Tax Residency"><input type="text" value={form.fatca_individual.country_of_tax_residency} onChange={e => setNested('fatca_individual', 'country_of_tax_residency', e.target.value)} className={inputClass()} /></Field>
                <Field label="TIN Type"><input type="text" value={form.fatca_individual.tin_type} onChange={e => setNested('fatca_individual', 'tin_type', e.target.value)} className={inputClass()} /></Field>
                <Field label="TIN"><input type="text" value={form.fatca_individual.tin} onChange={e => setNested('fatca_individual', 'tin', e.target.value)} className={inputClass()} /></Field>
                <Field label="Remarks if TIN Unavailable"><input type="text" value={form.fatca_individual.remarks_if_tin_unavailable} onChange={e => setNested('fatca_individual', 'remarks_if_tin_unavailable', e.target.value)} className={inputClass()} /></Field>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Field label="Is US Person"><select value={form.fatca_non_individual.is_us_person} onChange={e => setNested('fatca_non_individual', 'is_us_person', e.target.value)} className={inputClass()}>{yesNo.map(v => <option key={v}>{v}</option>)}</select></Field>
                {form.fatca_non_individual.is_us_person === 'Yes' && (
                  <Field label="US TIN"><input type="text" value={form.fatca_non_individual.us_tin} onChange={e => setNested('fatca_non_individual', 'us_tin', e.target.value)} className={inputClass()} /></Field>
                )}
                <Field label="Formed Outside India"><select value={form.fatca_non_individual.formed_outside_india} onChange={e => setNested('fatca_non_individual', 'formed_outside_india', e.target.value)} className={inputClass()}>{yesNo.map(v => <option key={v}>{v}</option>)}</select></Field>
                <Field label="Tax Residency Other Than India"><select value={form.fatca_non_individual.tax_residency_other_than_india} onChange={e => setNested('fatca_non_individual', 'tax_residency_other_than_india', e.target.value)} className={inputClass()}>{yesNo.map(v => <option key={v}>{v}</option>)}</select></Field>
                <Field label="Is Financial Institution"><select value={form.fatca_non_individual.is_financial_institution} onChange={e => setNested('fatca_non_individual', 'is_financial_institution', e.target.value)} className={inputClass()}>{yesNo.map(v => <option key={v}>{v}</option>)}</select></Field>
                {form.fatca_non_individual.is_financial_institution === 'Yes' && (
                  <Field label="GIIN"><input type="text" value={form.fatca_non_individual.giin} onChange={e => setNested('fatca_non_individual', 'giin', e.target.value)} className={inputClass()} /></Field>
                )}
                <Field label="Publicly Traded"><select value={form.fatca_non_individual.is_publicly_traded} onChange={e => setNested('fatca_non_individual', 'is_publicly_traded', e.target.value)} className={inputClass()}>{yesNo.map(v => <option key={v}>{v}</option>)}</select></Field>
                <Field label="Controlling Persons Not Indian"><select value={form.fatca_non_individual.controlling_persons_not_indian} onChange={e => setNested('fatca_non_individual', 'controlling_persons_not_indian', e.target.value)} className={inputClass()}>{yesNo.map(v => <option key={v}>{v}</option>)}</select></Field>
              </div>
            )}
          </Section>

          <div className="flex flex-col gap-3 pt-4 border-t">
            {isEdit && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="change-reason-block">
                <label className="block text-sm font-semibold text-amber-900 mb-1">
                  Reason for change <span className="text-red-600">*</span> <span className="text-amber-700 font-normal">(required — saved to Change History)</span>
                </label>
                <textarea
                  value={changeReason}
                  onChange={(e) => setChangeReason(e.target.value)}
                  rows={2}
                  className={`w-full border rounded px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-amber-500 focus:border-transparent ${errors.change_reason ? 'border-red-500' : 'border-amber-300'}`}
                  placeholder="Briefly describe why you are updating these details. Applied only to the section(s) that changed."
                  data-testid="change-reason-input"
                />
                {errors.change_reason && (
                  <p className="text-red-600 text-xs mt-1" data-testid="change-reason-error">{errors.change_reason}</p>
                )}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => navigate('/investors/accounts')} className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={submitting} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 inline-flex items-center" data-testid="submit-btn">
                <Save size={18} className="mr-2" /> {submitting ? 'Saving...' : isEdit ? 'Update' : 'Create Account'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Helper Components ---
const inputClass = (err) => `w-full border ${err ? 'border-red-500' : 'border-gray-300'} rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500`;
const Section = ({ title, children }) => (<section><h2 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b">{title}</h2>{children}</section>);
const Field = ({ label, error, children }) => (<div><label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>{children}{error && <p className="text-red-500 text-xs mt-1">{error}</p>}</div>);

const ContactPersonList = ({ title, type, list, onAdd, onUpdate, onRemove, sameAsContact, onSameAsContactChange, hasLinkedContact }) => (
  <div className="mb-6">
    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-3">
      <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
      <button type="button" onClick={() => onAdd(type)} className="text-sm text-blue-600 hover:text-blue-800 font-medium">+ Add Contact Person</button>
    </div>
    {hasLinkedContact && (
      <label className="flex items-center gap-2 mb-3 text-sm text-gray-600 cursor-pointer" data-testid={`${type}-same-as-contact-checkbox`}>
        <input
          type="checkbox"
          checked={sameAsContact}
          onChange={e => onSameAsContactChange(e.target.checked)}
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        Same as Investor Contact in Account Information
      </label>
    )}
    {list.length === 0 ? <p className="text-xs text-gray-400">No contacts added</p> : (
      <div className="space-y-4">
        {list.map((p, idx) => (
          <div key={p.id || idx} className="bg-gray-50 p-4 rounded-lg border space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-0.5 block">Salutation</label>
                <select value={p.salutation || ''} onChange={e => onUpdate(type, idx, 'salutation', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" data-testid={`${type}-${idx}-salutation`}>
                  <option value="">—</option>
                  <option value="Mr.">Mr.</option>
                  <option value="Mrs.">Mrs.</option>
                  <option value="Ms.">Ms.</option>
                  <option value="Dr.">Dr.</option>
                  <option value="Prof.">Prof.</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-0.5 block">First Name</label>
                <input type="text" placeholder="First Name" value={p.first_name || ''} onChange={e => onUpdate(type, idx, 'first_name', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" data-testid={`${type}-${idx}-first-name`} />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-0.5 block">Last Name</label>
                <input type="text" placeholder="Last Name" value={p.last_name || ''} onChange={e => onUpdate(type, idx, 'last_name', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" data-testid={`${type}-${idx}-last-name`} />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-0.5 block">Email ID</label>
                <input type="email" placeholder="Email" value={p.email || ''} onChange={e => onUpdate(type, idx, 'email', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" data-testid={`${type}-${idx}-email`} />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-0.5 block">Mobile No</label>
                <div className="flex gap-1.5">
                  <input
                    type="text"
                    placeholder="+91"
                    value={p.mobile_country_code || ''}
                    onChange={e => onUpdate(type, idx, 'mobile_country_code', e.target.value)}
                    className="w-16 border rounded px-2 py-1.5 text-sm font-mono"
                    title="Country code with leading + (e.g. +91, +65, +1)"
                    data-testid={`${type}-${idx}-mobile-cc`}
                  />
                  <input
                    type="text"
                    placeholder="98765 43210"
                    value={p.mobile || ''}
                    onChange={e => onUpdate(type, idx, 'mobile', e.target.value)}
                    className="flex-1 border rounded px-2 py-1.5 text-sm"
                    title="Local mobile number (without country code)"
                    data-testid={`${type}-${idx}-mobile`}
                  />
                </div>
                <p className="text-[10px] text-gray-400 mt-0.5">Split fields: country code (with +) and local number — ensures WhatsApp delivery for non-+91 numbers.</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-0.5 block">Telephone Number</label>
                <input type="text" placeholder="Telephone" value={p.telephone || ''} onChange={e => onUpdate(type, idx, 'telephone', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" data-testid={`${type}-${idx}-telephone`} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-3">
              <div className="md:col-span-2">
                <label className="text-xs text-gray-500 mb-0.5 block flex items-center gap-1">
                  WhatsApp No
                  <span className="text-[10px] text-gray-400">(leave blank to use Mobile)</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. +91 98xxxxxxxx"
                  value={p.whatsapp_number || ''}
                  onChange={e => onUpdate(type, idx, 'whatsapp_number', e.target.value)}
                  className="w-full border rounded px-2 py-1.5 text-sm"
                  data-testid={`contact-whatsapp-number-${type}-${idx}`}
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-0.5 block">WhatsApp Opt-out</label>
                <select
                  value={p.whatsapp_opt_out ? 'Yes' : 'No'}
                  onChange={e => onUpdate(type, idx, 'whatsapp_opt_out', e.target.value === 'Yes')}
                  className={`w-full border rounded px-2 py-1.5 text-sm ${p.whatsapp_opt_out ? 'bg-red-50 border-red-300 text-red-700 font-medium' : ''}`}
                  data-testid={`contact-whatsapp-optout-${type}-${idx}`}
                  title={p.whatsapp_opt_out ? 'WhatsApp sends will skip this contact.' : 'WhatsApp sends will include this contact.'}
                >
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-0.5 block">Authorised Signatory</label>
                <select value={p.authorised_signatory || 'No'} onChange={e => onUpdate(type, idx, 'authorised_signatory', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" data-testid={`${type}-${idx}-signatory`}>
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </div>
              {p.authorised_signatory === 'Yes' && (
                <div>
                  <label className="text-xs text-gray-500 mb-0.5 block">Position / Title of Signatory</label>
                  <input type="text" placeholder="e.g. Director, Partner" value={p.signatory_position || ''} onChange={e => onUpdate(type, idx, 'signatory_position', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" data-testid={`${type}-${idx}-signatory-position`} />
                </div>
              )}
              <div className={`${p.authorised_signatory === 'Yes' ? 'md:col-span-2' : 'md:col-span-3'}`}>
                <label className="text-xs text-gray-500 mb-0.5 block">Remarks</label>
                <textarea placeholder="Remarks..." value={p.remarks || ''} onChange={e => onUpdate(type, idx, 'remarks', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" rows={2} data-testid={`${type}-${idx}-remarks`} />
              </div>
            </div>
            <div className="flex justify-end">
              <button type="button" onClick={() => onRemove(type, idx)} className="text-red-500 hover:text-red-700 text-xs font-medium px-2 py-1 hover:bg-red-50 rounded" data-testid={`${type}-${idx}-remove`}>
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const AddressBlock = ({ title, section, data, onChange }) => (
  <div>
    <h3 className="text-sm font-semibold text-gray-700 mb-2">{title}</h3>
    <div className="space-y-2">
      <textarea placeholder="Address" value={data.address || ''} onChange={e => onChange(section, 'address', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" rows={2} />
      <div className="grid grid-cols-2 gap-2">
        <input type="text" placeholder="City" value={data.city || ''} onChange={e => onChange(section, 'city', e.target.value)} className="border rounded px-2 py-1.5 text-sm" />
        <input type="text" placeholder="State" value={data.state || ''} onChange={e => onChange(section, 'state', e.target.value)} className="border rounded px-2 py-1.5 text-sm" />
        <input type="text" placeholder="Country" value={data.country || ''} onChange={e => onChange(section, 'country', e.target.value)} className="border rounded px-2 py-1.5 text-sm" />
        <input type="text" placeholder="ZIP Code" value={data.zip_code || ''} onChange={e => onChange(section, 'zip_code', e.target.value)} className="border rounded px-2 py-1.5 text-sm" />
      </div>
      <textarea placeholder="Remarks" value={data.remarks || ''} onChange={e => onChange(section, 'remarks', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" rows={2} />
    </div>
  </div>
);

export default InvestorAccountForm;
